import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ExternalLink, Newspaper } from "lucide-react";
import { useState } from "react";
import { api, type NewsItem } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CategoryBadge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PriceAtTime } from "@/components/PriceAtTime";
import { SummarizeButton } from "@/components/SummarizeButton";
import { useSeo } from "@/lib/useSeo";
import { cn, fromNowFR } from "@/lib/utils";

// Activity buckets — same thresholds as the Go API (ListForums / GlobalNewsList).
function scoreCategory(score: number | null, dead: boolean): string {
  if (dead) return "dead";
  if (score == null) return "unknown";
  if (score >= 60) return "hot";
  if (score >= 30) return "active";
  if (score >= 10) return "quiet";
  return "dormant";
}

const FILTERS: { key: string; label: string }[] = [
  { key: "all", label: "Tout" },
  { key: "hot", label: "Chaud" },
  { key: "active", label: "Actif" },
  { key: "quiet", label: "Calme" },
];

export default function NewsPage() {
  useSeo({
    title: "Actualités des valeurs Boursorama",
    description:
      "Le fil d'actualités agrégé des forums Boursorama : news par valeur, source, date et cours à la publication. Repérez ce qui fait bouger les cours.",
    canonicalPath: "/news",
  });
  const [category, setCategory] = useState("all");

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["news", category],
      queryFn: ({ pageParam }) =>
        api.news({ category, limit: 50, before: pageParam || undefined }),
      initialPageParam: "",
      getNextPageParam: (last) => last.next_cursor || undefined,
    });

  const items: NewsItem[] = data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Newspaper className="size-5" />
          Actualités
        </h1>
        <p className="text-sm text-foreground-muted">
          Le flux agrégé de toutes les actualités scrapées, tous forums confondus.
        </p>
      </header>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <Button
            key={f.key}
            size="sm"
            variant={category === f.key ? "default" : "outline"}
            onClick={() => setCategory(f.key)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive">
          Impossible de charger les actualités. Réessayez plus tard.
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-foreground-muted">
          Aucune actualité pour ce filtre.
        </p>
      ) : (
        <>
          <ul className="flex flex-col gap-3">
            {items.map((n) => (
              <li key={`${n.id}-${n.url}`}>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <a
                          href={n.url}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium leading-snug hover:underline line-clamp-2"
                        >
                          {n.title}
                        </a>
                        {n.description && (
                          <p className="text-sm text-foreground-muted mt-1.5 line-clamp-2">
                            {n.description}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-muted mt-2">
                          <CategoryBadge category={scoreCategory(n.active_score, n.dead)} />
                          <Link
                            to={`/forums/${n.forum_id}/threads`}
                            className="hover:underline font-medium text-foreground"
                          >
                            {n.forum_title}
                          </Link>
                          {n.source && (
                            <>
                              <span>·</span>
                              <span>{n.source}</span>
                            </>
                          )}
                          <span>·</span>
                          <span>{fromNowFR(n.published_at)}</span>
                          <PriceAtTime
                            price={n.price_at_publish}
                            variation={n.variation_at_publish}
                            currency={n.price_currency}
                            source={n.price_source}
                          />
                        </div>
                        <div className="mt-2">
                          <SummarizeButton kind="news" id={n.id} label="Résumer" />
                        </div>
                      </div>
                      <a
                        href={n.url}
                        target="_blank"
                        rel="noreferrer"
                        className={cn(
                          "shrink-0 text-foreground-muted hover:text-foreground transition-colors"
                        )}
                        aria-label="Ouvrir l'article"
                      >
                        <ExternalLink className="size-4" />
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>

          {hasNextPage && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? "Chargement…" : "Charger plus"}
              </Button>
            </div>
          )}
          {!hasNextPage && items.length > 0 && (
            <p className="text-center text-xs text-foreground-muted py-2">
              Fin du flux ({items.length} actualités)
            </p>
          )}
        </>
      )}
    </div>
  );
}
